import pkg from "../../package.json"

/**
 * Anonymous product analytics for the web app, sent to Paisa's own API (POST /api/events). Same wire format
 * and rules as the phone: UI facts only (the screen), a random install id kept in this browser, never linked
 * to the account. Off entirely when the browser sends Global Privacy Control or Do Not Track.
 */
const KEY = "paisa-analytics-id"
const FLUSH_MS = 30_000
const MAX = 100

type Queued = { name: string; props: Record<string, string>; at: number }
let queue: Queued[] = []

/** True when the browser asks not to be tracked. */
export function optedOut(nav: {
  globalPrivacyControl?: boolean
  doNotTrack?: string | null
}): boolean {
  return nav.globalPrivacyControl === true || nav.doNotTrack === "1"
}

/** A path as an event value: ids and numbers become ":id" so screens group ("/goals/:id"). */
export function screenName(path: string): string {
  return (
    path
      .split("/")
      .map((p) => (/^[0-9a-f-]{8,}$/i.test(p) || /^\d+$/.test(p) ? ":id" : p))
      .join("/")
      .replace(/[^A-Za-z0-9_.:/+-]/g, "")
      .slice(0, 60) || "/"
  )
}

function installId(): string {
  try {
    let id = localStorage.getItem(KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(KEY, id)
      queue.push({ name: "install", props: {}, at: Date.now() })
    }
    return id
  } catch {
    return crypto.randomUUID()
  }
}

function flush(beacon = false) {
  if (!queue.length) return
  const body = JSON.stringify({
    installId: installId(),
    appVersion: pkg.version,
    platform: "web",
    locale: navigator.language,
    events: queue.slice(0, 50),
  })
  queue = queue.slice(50)
  if (beacon && navigator.sendBeacon) {
    navigator.sendBeacon("/api/events", new Blob([body], { type: "application/json" }))
    return
  }
  // Fire and forget: a failed send just loses those events.
  void fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body,
    keepalive: true,
  }).catch(() => {})
}

let started = false

/** One product event with short-code props (UI facts only); dropped when analytics is off. */
export function track(name: string, props: Record<string, string>) {
  if (!started) return
  queue = [...queue, { name, props, at: Date.now() }].slice(-MAX)
}

/** Call once at startup; returns the tracker for a screen (no-op when opted out). */
export function startAnalytics(): (path: string) => void {
  if (started || typeof navigator === "undefined" || optedOut(navigator as never)) return () => {}
  started = true
  installId()
  setInterval(() => flush(), FLUSH_MS)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true)
  })
  return (path) => {
    queue = [...queue, { name: "screen", props: { name: screenName(path) }, at: Date.now() }].slice(
      -MAX,
    )
  }
}
